import { Award, ExternalLink, Sparkles, MessageSquare, GraduationCap } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { certifications } from "@/data/certifications";
import { certificationsTranslations } from "@/data/certificationsTranslations";

const Certifications = () => {
  const { language, t } = useLanguage();

  const getAreaIcon = (area: string) => {
    switch (area) {
      case "ai":
        return <Sparkles className="h-5 w-5" />;
      case "ux":
        return <MessageSquare className="h-5 w-5" />;
      case "communication":
        return <GraduationCap className="h-5 w-5" />;
      default:
        return <Award className="h-5 w-5" />;
    }
  };

  const getAreaLabel = (area: string) => {
    const labels = {
      pt: {
        ai: "Inteligência Artificial",
        ux: "UX Design",
        communication: "Comunicação",
      },
      en: {
        ai: "Artificial Intelligence",
        ux: "UX Design",
        communication: "Communication",
      },
    };
    return labels[language][area as keyof typeof labels.pt] || area;
  };

  const getAreaColor = (area: string) => {
    switch (area) {
      case "ai":
        return "bg-primary/10 text-primary border-primary/20";
      case "ux":
        return "bg-secondary/10 text-secondary-foreground border-secondary/20";
      case "communication":
        return "bg-accent/10 text-accent-foreground border-accent/20";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  return (
    <section id="certifications" className="py-20 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            {t("certifications.title")}
          </h2>
          <p className="text-muted-foreground text-lg">
            {t("certifications.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {certifications.map((cert, index) => {
            const translation = certificationsTranslations[language][cert.id];
            return (
              <Card
                key={cert.id}
                className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-border/50 bg-card/50 backdrop-blur-sm animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardHeader>
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className={`p-2 rounded-lg ${getAreaColor(cert.area)}`}>
                      {getAreaIcon(cert.area)}
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {cert.year}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl group-hover:text-primary transition-colors">
                    {translation.title}
                  </CardTitle>
                  <CardDescription className="text-sm font-medium text-muted-foreground">
                    {cert.institution}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    {translation.description}
                  </p>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {getAreaLabel(cert.area)}
                    </Badge>
                  </div>
                  <div className="mt-4 flex gap-2">
                    {cert.certificateUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="flex-1"
                      >
                        <a
                          href={cert.certificateUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Award className="h-4 w-4 mr-2" />
                          {t("certifications.viewCertificate")}
                        </a>
                      </Button>
                    )}
                    {cert.verificationUrl && (
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                      >
                        <a
                          href={cert.verificationUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Certifications;
